import { useState } from 'react';
import IUserMenu from '../../interfaces/IUserMenu';
import { IUserRole } from '../../interfaces/IUserRole';
import localStorageService from '../../services/LocalStorage';
import { tokenService } from '../../services/TokenService';
import { showToast } from '../../utils/commonUtility';
import useCustomerSignIn from '../graphql/useCustomerSignIn';
import useGetCompany from '../graphql/useGetCompany';
import { useGetMenus } from '../graphql/useGetMenus';

interface CompanyData {
  abbreviatedCompanyName: string;
  companyCategoryId: string;
  companyCode: string;
  companyColourCode: string;
  companyId: number;
  isArchived: number;
  logo: string;
}

export interface UserInfo {
  accessRights: number;
  active: boolean;
  companyId: number;
  dialCode: string;
  email: string;
  emailVerificationStatus: 'verified' | 'unverified';
  empRfidNo: string;
  employeeId: number;
  enableFavouriteProductNotification: boolean;
  familyName: string;
  feelEatPhoneNumber: string;
  firstName: string;
  isAllergiesEnable: boolean;
  isPreferencesEnable: boolean;
  mobile: string;
  mobileVerificationStatus: 'verified' | 'unverified';
  userName: string;
}

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    Boolean(tokenService.getToken()),
  );

  const [companyInfo, setCompanyInfo] = useState<CompanyData | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(
    tokenService.getUserInfo(),
  );
  const [currentUserRole, setCurrentUserRole] = useState<IUserRole | null>(
    tokenService.getUserRole(),
  );
  const [userRoles, setUserRoles] = useState<IUserRole[]>(
    tokenService.getUserRoles(),
  );
  const [platformId, setPlatformId] = useState<number | null>(
    tokenService.getPlatformId(),
  );
  const [userMenus, setUserMenus] = useState<IUserMenu[]>(
    tokenService.getUserMenus(),
  );
  const [userPermissions, setUserPermissions] = useState<string[]>(
    tokenService.getUserPermissions(),
  );
  const [needToVerifyToken, setNeedToVerify] = useState(
    Boolean(tokenService.getToken()),
  );

  const { signIn, loading: signInLoading } = useCustomerSignIn();
  const { getUserMenus, loading: menusLoading } = useGetMenus();

  const {
    getCompanyProfile,
    error: companyError,
    loading: companyLoading,
  } = useGetCompany();

  const handleAfterLogin = (accessToken: string): boolean => {
    if (!accessToken) {
      showToast('fail-login', 'error', '', 'topLeft');
      return false;
    }
    showToast('success-login', 'success', '', 'topLeft');
    tokenService.setToken(accessToken);
    setIsAuthenticated(true);
    return true;
  };

  const handleUserInfo = (info: UserInfo) => {
    tokenService.setUserInfo(info);
    setUserInfo(info);
  };

  const handleUserRole = (role: IUserRole) => {
    tokenService.setUserRole(role);
    setCurrentUserRole(role);
  };

  const loginCta = async (email: string, appName: string, password: string) => {
    const res = await signIn(email, appName, password);
    const signInData = res?.data?.employeeSignIn?.data;

    if (!handleAfterLogin(signInData?.token)) return;

    handleUserInfo(signInData.user);

    const roles = signInData.roles || [];
    setUserRoles(roles); // Save all roles
    tokenService.setUserRoles(roles);

    const defaultRole: IUserRole =
      roles.find((role: IUserRole) => role.isDefault) || roles[0];

    if (!defaultRole || !signInData.platformId) return;

    handleUserRole(defaultRole);
    setPlatformId(signInData.platformId);
    tokenService.setPlatformId(signInData.platformId);

    try {
      const menusRes = await getUserMenus({
        variables: {
          params: {
            platformId: signInData.platformId,
          },
        },
        fetchPolicy: 'no-cache',
      });

      const menuData = menusRes?.data?.getUserMenus?.data;

      if (menuData) {
        setUserMenus(menuData.mainMenus || []);
        setUserPermissions(menuData.rolePermissions || []);

        tokenService.setUserMenus(menuData.mainMenus || []);
        tokenService.setUserPermissions(menuData.rolePermissions || []);
      }
    } catch (error) {
      console.error('Error fetching user menus:', error);
    }

    verifyUser(true);
  };

  const verifyUser = async (verify = false) => {
    if (needToVerifyToken || verify) {
      const res = await getCompanyProfile();
      if (res?.data?.getCompany?.data) {
        setIsAuthenticated(true);
        setCompanyInfo(res.data.getCompany.data);
      } else {
        tokenService.disposeToken();
        setIsAuthenticated(false);
      }
    }
    setNeedToVerify(false);
  };

  const clearUserSession = () => {
    tokenService.disposeAllTokens();
    localStorageService.clear();

    setIsAuthenticated(false);
    setUserInfo(null);
    setCurrentUserRole(null);
    setUserRoles([]);
    tokenService.deleteUserRoles();
    setPlatformId(null);
    setUserMenus([]);
    setUserPermissions([]);
  };

  const switchUserRole = async (roleId: number) => {
    const role = userRoles.find(r => r.roleId === roleId);
    if (!role || !platformId) return;

    handleUserRole(role);
    tokenService.setUserRole(role);

    try {
      const menusRes = await getUserMenus({
        variables: {
          params: {
            platformId,
          },
        },
        fetchPolicy: 'no-cache',
      });

      const menuData = menusRes?.data?.getUserMenus?.data;

      if (menuData) {
        const mainMenus = menuData.mainMenus || [];
        const rolePermissions = menuData.rolePermissions || [];

        setUserMenus(mainMenus);
        setUserPermissions(rolePermissions);

        tokenService.setUserMenus(mainMenus);
        tokenService.setUserPermissions(rolePermissions);

        return mainMenus.length > 0 ? mainMenus[0].path : null;
      }
    } catch (error) {
      console.error('Error switching user role:', error);
    }
  };

  const state = {
    isAuthenticated,
    needToVerifyToken,
    signInLoading,
    menusLoading,
    companyError,
    companyLoading,
    companyInfo,
    userInfo,
    currentUserRole,
    userRoles,
    platformId,
    userMenus,
    userPermissions,
  };

  const actions = {
    loginCta,
    verifyUser,
    clearUserSession,
    switchUserRole,
  };

  return { state, actions };
};

export default useAuth;
